import { memo, type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { formatBytes, formatDuration } from '@air/utilities';

import { AssetTitle } from '~/components/AssetModalV2/components/AssetTitle';
import { EditiableInfoPanelTitle } from '~/components/AssetModalV2/InfoPanel/components/EditableInfoPanelTitle';
import { INFO_PANEL_FILE_EXTENSION } from '~/constants/testIDs';
import {
  centralizedClipDimensionsSelector,
  centralizedClipDurationSelector,
  centralizedClipExtSelector,
  centralizedClipSizeSelector,
} from '~/store/centralizedClip/selectors';

export type InfoPanelAssetMetadataProps = {
  canEdit: boolean;
  versionChip?: ReactNode;
};

export const InfoPanelAssetMetadata = memo(({ canEdit, versionChip }: InfoPanelAssetMetadataProps) => {
  const dimensions = useSelector(centralizedClipDimensionsSelector);
  const extension = useSelector(centralizedClipExtSelector);
  const duration = useSelector(centralizedClipDurationSelector);
  const size = useSelector(centralizedClipSizeSelector);

  const formattedDuration = duration ? formatDuration(duration) : null;
  const formattedSize = formatBytes(size);
  const formattedExtension = extension?.toUpperCase();
  const formattedDimensions =
    dimensions.width && dimensions.height ? `${dimensions.width} x ${dimensions.height}` : null;

  return (
    <div className="flex w-full flex-col gap-1 ads:text-metadata text-gray-10">
      <div className="w-full">{canEdit ? <EditiableInfoPanelTitle /> : <AssetTitle />}</div>
      <div className="flex items-center gap-2 font-bold text-10 tracking-[0.04em]">
        {versionChip}
        {!!formattedExtension && <span data-testid={INFO_PANEL_FILE_EXTENSION}>{formattedExtension}</span>}
        {!!formattedSize && <span>{formattedSize}</span>}
        {!!formattedDimensions && <span>{formattedDimensions}</span>}
        {!!formattedDuration && <span>{formattedDuration}</span>}
      </div>
    </div>
  );
});

InfoPanelAssetMetadata.displayName = 'InfoPanelAssetMetadata';
